import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, HostListener, inject, OnInit, signal, computed } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { FileUploadModule } from 'primeng/fileupload';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { GlobalOverlayService } from '../../../core/ui/global-overlay.service';
import { StatusBadge } from '../../../shared/components/status-badge.component';
import { RefreshButtonComponent } from '../../../shared/components/refresh-button.component';
import { Customer, CustomerService, CustomerType } from '../services/customer.service';
import { AuthService } from '../../../core/auth/auth.service';
import { canAccess, CUSTOMER_ACTION_ACCESS } from '../../../core/auth/access-control';

@Component({
  selector: 'app-customers-page',
  imports: [
    DatePipe,
    ReactiveFormsModule,
    LucideAngularModule,
    StatusBadge,
    RefreshButtonComponent,
    TableModule,
    FileUploadModule,
    InputTextModule,
    SelectModule,
    TextareaModule,
  ],
  templateUrl: './customers-page.component.html',
})
export class CustomersPageComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly overlay = inject(GlobalOverlayService);
  private readonly customerService = inject(CustomerService);
  private readonly auth = inject(AuthService);

  readonly canCreateCustomer = computed(() =>
    canAccess(this.auth.user(), CUSTOMER_ACTION_ACCESS.create),
  );

  readonly canEditCustomer = computed(() =>
    canAccess(this.auth.user(), CUSTOMER_ACTION_ACCESS.edit),
  );

  readonly canDeleteCustomer = computed(() =>
    canAccess(this.auth.user(), CUSTOMER_ACTION_ACCESS.delete),
  );

  readonly isCustomerModalOpen = signal(false);
  readonly isSaving = signal(false);
  readonly isLoadingCustomers = signal(false);
  readonly selectedFile = signal<File | null>(null);
  readonly editingCustomer = signal<Customer | null>(null);
  readonly customers = signal<Customer[]>([]);
  readonly selectedCustomer = signal<Customer | null>(null);

  readonly customerTypeOptions = [
    { label: 'บุคคลทั่วไป', value: 'INDIVIDUAL' as CustomerType },
    { label: 'บริษัท / นิติบุคคล', value: 'CORPORATE' as CustomerType },
  ];

  ngOnInit(): void {
    void this.loadCustomers();
  }

  async loadCustomers(): Promise<void> {
    if (this.isLoadingCustomers()) return;
    this.isLoadingCustomers.set(true);
    this.overlay.showLoading('กำลังโหลดข้อมูลลูกค้า...');
    try {
      this.customers.set(await this.customerService.getAll());
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'ไม่สามารถโหลดข้อมูลลูกค้าได้';
      this.overlay.error('โหลดข้อมูลไม่สำเร็จ', message);
    } finally {
      this.isLoadingCustomers.set(false);
      this.overlay.hideLoading();
    }
  }

  /* filterCustomers(term: string): void {
    const query = term.trim().toLowerCase();
    this.customers.set(
      query
        ? CUSTOMERS.filter((row) => `${row.customerCode} ${row.name} ${row.phone}`.toLowerCase().includes(query))
        : CUSTOMERS,
    );
  } */

  readonly customerForm = this.fb.nonNullable.group({
    customerType: this.fb.nonNullable.control<CustomerType>('INDIVIDUAL', Validators.required),
    name: ['', [Validators.required, Validators.maxLength(150)]],
    taxId: ['', [Validators.minLength(9), Validators.maxLength(13)]],
    phone: ['', [Validators.required, Validators.pattern(/^[0-9+\- ]{9,20}$/)]],
    email: ['', Validators.email],
    lineId: [''],
    address: [''],
  });

  openCustomerModal(): void {
    this.editingCustomer.set(null);
    this.customerForm.reset({ customerType: 'INDIVIDUAL' });
    this.isCustomerModalOpen.set(true);
  }

  openEditCustomerModal(customer: Customer): void {
    if (customer.status === 'DELETED') {
      this.overlay.error('ไม่สามารถแก้ไขได้', 'ลูกค้ารายนี้ถูกลบแล้ว');
      return;
    }
    this.editingCustomer.set(customer);
    this.selectedFile.set(null);
    this.customerForm.reset({
      customerType: customer.customerType,
      name: customer.name,
      taxId: customer.taxId ?? '',
      phone: customer.phone,
      email: customer.email ?? '',
      lineId: customer.lineId ?? '',
      address: customer.address ?? '',
    });
    this.isCustomerModalOpen.set(true);
  }

  closeCustomerModal(): void {
    this.isCustomerModalOpen.set(false);
    this.selectedFile.set(null);
    this.editingCustomer.set(null);
  }

  onFileSelect(event: { files: File[] }): void {
    this.selectedFile.set(event.files[0] ?? null);
  }

  onFileClear(): void {
    this.selectedFile.set(null);
  }

  attachmentUrl(fileUrl: string): string {
    return this.customerService.resolveFileUrl(fileUrl);
  }

  openCustomerDetail(customer: Customer): void {
    this.selectedCustomer.set(customer);
  }
  closeCustomerDetail(): void {
    this.selectedCustomer.set(null);
  }

  async deleteCustomer(customer: Customer): Promise<void> {
    if (customer.status === 'DELETED') return;

    const confirmed = await this.overlay.confirm({
      title: 'ยืนยันการลบลูกค้า',
      message: `ต้องการลบ ${customer.name} ใช่หรือไม่? ข้อมูลจะถูกเปลี่ยนสถานะเป็นถูกลบ`,
      confirmText: 'ลบลูกค้า',
      cancelText: 'ยกเลิก',
    });
    if (!confirmed) return;

    this.overlay.showLoading('กำลังลบลูกค้า...');
    try {
      const deletedCustomer = await this.customerService.delete(customer.id);
      this.customers.update((customers) =>
        customers.map((item) => (item.id === deletedCustomer.id ? deletedCustomer : item)),
      );
      if (this.selectedCustomer()?.id === deletedCustomer.id) {
        this.selectedCustomer.set(deletedCustomer);
      }
      this.overlay.success('ลบลูกค้าสำเร็จ', 'เปลี่ยนสถานะลูกค้าเป็นถูกลบแล้ว');
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'ไม่สามารถลบลูกค้าได้';
      this.overlay.error('ลบลูกค้าไม่สำเร็จ', message);
    }
  }

  async saveCustomer(): Promise<void> {
    if (this.customerForm.invalid) {
      this.customerForm.markAllAsTouched();
      return;
    }

    this.isSaving.set(true);
    const editingCustomer = this.editingCustomer();
    this.overlay.showLoading(
      editingCustomer ? 'กำลังแก้ไขข้อมูลลูกค้า...' : 'กำลังบันทึกข้อมูลลูกค้า...',
    );
    try {
      const formValue = this.customerForm.getRawValue();
      const customer = editingCustomer
        ? await this.customerService.update(editingCustomer.id, formValue, this.selectedFile())
        : await this.customerService.create(formValue, this.selectedFile());

      this.customers.update((customers) =>
        editingCustomer
          ? customers.map((item) => (item.id === customer.id ? customer : item))
          : [customer, ...customers],
      );
      this.closeCustomerModal();
      this.overlay.success(
        editingCustomer ? 'แก้ไขลูกค้าสำเร็จ' : 'เพิ่มลูกค้าสำเร็จ',
        editingCustomer ? 'อัปเดตข้อมูลลูกค้าเรียบร้อยแล้ว' : 'บันทึกข้อมูลลูกค้าเรียบร้อยแล้ว',
      );
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'ไม่สามารถบันทึกข้อมูลลูกค้าได้';
      this.overlay.error(
        editingCustomer ? 'แก้ไขลูกค้าไม่สำเร็จ' : 'เพิ่มลูกค้าไม่สำเร็จ',
        message,
      );
    } finally {
      this.isSaving.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isCustomerModalOpen()) this.closeCustomerModal();
    if (this.selectedCustomer()) this.closeCustomerDetail();
  }
}
